(() => {
  if (window.__ottoFreeShippingInit) return;
  window.__ottoFreeShippingInit = true;

  const DISMISS_KEY = 'otto-gap-closer-dismissed';
  const CART_WRITE = /\/cart\/(add|change|update|clear)/i;
  const dismissed = () => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch (_) { return false; }
  };
  const routeRoot = () => window.Shopify?.routes?.root || '/';

  const publish = (eventName, payload) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, payload));
  };

  const MONEY_PLACEHOLDERS = {
    amount: { decimals: 2, thousands: ',', decimal: '.' },
    amount_no_decimals: { decimals: 0, thousands: ',', decimal: '.' },
    amount_with_comma_separator: { decimals: 2, thousands: '.', decimal: ',' },
    amount_no_decimals_with_comma_separator: { decimals: 0, thousands: '.', decimal: ',' },
    amount_with_apostrophe_separator: { decimals: 2, thousands: "'", decimal: '.' },
  };

  /*
    Mirrors the Liquid `money` filter so JS-rendered prices match the rest of
    the cart. Shopify.formatMoney is not loaded on this storefront.
  */
  const formatMoney = (cents, moneyFormat) => {
    const value = Number(cents) || 0;
    const format = moneyFormat || '{{amount_with_comma_separator}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
      const rule = MONEY_PLACEHOLDERS[placeholder] || MONEY_PLACEHOLDERS.amount_with_comma_separator;
      const [whole, fraction] = (value / 100).toFixed(rule.decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, rule.thousands);
      return fraction ? `${grouped}${rule.decimal}${fraction}` : grouped;
    });
  };

  const toCents = (raw) => {
    if (raw == null || raw === '') return 0;
    if (typeof raw === 'number') {
      if (!Number.isInteger(raw)) return Math.round(raw * 100);
      return raw;
    }
    const text = String(raw).trim().replace(',', '.');
    if (text.includes('.')) return Math.round(parseFloat(text) * 100);
    return parseInt(text, 10) || 0;
  };

  /*
    A suggestion is only useful if it can actually be bought, so a product
    without a purchasable variant is dropped. Both the recommendations and the
    products.json payloads carry per-variant availability; the product-level
    flag is only consulted when the variants do not report it.
  */
  const purchasableVariant = (product) => {
    if (product.available === false) return null;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const inStock = variants.find((variant) => variant.available === true);
    if (inStock) return inStock;
    const availabilityUnknown =
      variants.length > 0 && variants.every((variant) => variant.available == null);
    return availabilityUnknown && product.available === true ? variants[0] : null;
  };

  const variantPrice = (product, variant) =>
    toCents(variant?.price ?? product.price ?? product.price_min);

  const productImage = (product) => {
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image?.src) return product.featured_image.src;
    if (product.featured_image?.url) return product.featured_image.url;
    if (Array.isArray(product.images) && product.images[0]) {
      return typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src;
    }
    return '';
  };

  const productUrl = (product) => {
    if (product.url) return product.url;
    if (product.handle) return `${routeRoot()}products/${product.handle}`;
    return '#';
  };

  const parseProducts = async (url) => {
    if (!url) return [];
    const jsonUrl = new URL(url, window.location.origin);
    if (jsonUrl.pathname.endsWith('/recommendations/products')) jsonUrl.pathname += '.json';
    const response = await fetch(jsonUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    const data = await response.json();
    return data.products || [];
  };

  const isAccessoryType = (type) => {
    const value = String(type || '')
      .toLowerCase()
      .replace('ø', 'oe')
      .replace('æ', 'ae')
      .replace('å', 'aa');
    return (
      value === 'event' ||
      value === 'bundle' ||
      value === 'vintilbehor' ||
      value === 'tilbehor' ||
      value === 'glas' ||
      value === 'glass' ||
      value.includes('tilbehor')
    );
  };

  const pickGapProducts = (products, root) => {
    const minPrice = Number(root.dataset.minPrice || 0);
    const maxPrice = Number(root.dataset.maxPrice || 0);
    const exclude = root.dataset.excludeIds || '|';
    const desktopMax = Number(root.dataset.maxDesktop || 3);
    const wantsWines =
      root.dataset.filter === 'mix' ||
      root.dataset.filter === 'shipping-wines' ||
      root.dataset.filter === 'shipping-combined';
    const available = [];
    const overGap = [];

    products.forEach((product) => {
      if (!product) return;
      const idNeedle = `|${product.id}|`;
      if (exclude.includes(idNeedle)) return;
      const variant = purchasableVariant(product);
      if (!variant) return;
      const price = variantPrice(product, variant);
      const id = Number(variant.id || 0);
      if (!id || price <= 0) return;
      if (wantsWines && isAccessoryType(product.product_type || product.type)) return;
      const row = { product, price, id };
      if (wantsWines) {
        available.push(row);
        return;
      }
      if (price >= minPrice) overGap.push(row);
      if (price >= minPrice && price <= maxPrice) available.push(row);
    });

    if (wantsWines) return available.slice(0, desktopMax);
    const sortByPrice = (left, right) => left.price - right.price;
    const selected = (available.length ? available : overGap.sort(sortByPrice)).slice(0, desktopMax);
    return selected;
  };

  // Vintage-prefixed titles supplied by Liquid, keyed by product id.
  const parseTitles = (raw) => {
    if (!raw) return {};
    try {
      return JSON.parse(raw) || {};
    } catch (error) {
      return {};
    }
  };

  const renderGapRows = (root, rows) => {
    const list = root.querySelector('[data-gap-list]');
    const template = root.querySelector('[data-gap-row-template]');
    if (!list || !template) return;
    const addLabel = root.dataset.addLabel || '';
    const moneyFormat = root.dataset.moneyFormat;
    const titles = parseTitles(root.dataset.titles);
    const staticAccessories = Array.from(list.querySelectorAll('[data-static-accessory]')).map((node) =>
      node.cloneNode(true)
    );
    list.replaceChildren();
    rows.forEach(({ product, price, id }) => {
      const node = template.content.firstElementChild.cloneNode(true);
      node.dataset.productId = String(product.id);
      node.dataset.variantId = String(id);
      node.dataset.price = String(price);
      const media = node.querySelector('.cart-gap-closer__media');
      const img = node.querySelector('img');
      const title = node.querySelector('.cart-gap-closer__title');
      const priceEl = node.querySelector('.cart-gap-closer__price');
      const addBtn = node.querySelector('[data-gap-add]');
      const href = productUrl(product);
      const label = titles[String(product.id)] || product.title || '';
      if (media) media.setAttribute('href', href);
      if (title) {
        title.setAttribute('href', href);
        title.textContent = label;
      }
      if (img) {
        img.src = productImage(product);
        img.alt = label;
      }
      if (priceEl) priceEl.textContent = formatMoney(price, moneyFormat);
      if (addBtn) {
        addBtn.textContent = addLabel;
        addBtn.dataset.variantId = String(id);
        addBtn.dataset.productId = String(product.id);
        addBtn.dataset.price = String(price);
      }
      list.append(node);
    });
    staticAccessories.forEach((node) => list.append(node));
    if (!rows.length) root.hidden = true;
  };

  const loadGapCloser = async (root) => {
    if (!root || root.dataset.gapLoaded === '1' || root.hidden) return;
    const isMix = root.dataset.filter === 'mix';
    if (!isMix && dismissed()) {
      root.hidden = true;
      return;
    }
    root.dataset.gapLoaded = '1';
    if (!root.dataset.recsUrl && !root.dataset.fallbackUrl) return;
    const existingRows = root.querySelectorAll('[data-gap-list] .cart-gap-closer__row').length;
    if (root.dataset.filter === 'shipping-wines' && existingRows >= 2) return;
    try {
      let products = [];
      let rows = [];
      if (root.dataset.productId) {
        products = await parseProducts(root.dataset.recsUrl);
        rows = pickGapProducts(products, root);
      }
      if (rows.length < 2) {
        const fallback = await parseProducts(root.dataset.fallbackUrl);
        products = products.concat(fallback);
        rows = pickGapProducts(products, root);
      }
      if (rows.length) {
        renderGapRows(root, rows);
      } else if (!isMix && !root.querySelector('[data-gap-list] .cart-gap-closer__row')) {
        root.hidden = true;
      }
    } catch (error) {
      console.warn(error);
    }
  };

  const addGapProduct = async (button, root) => {
    const id = Number(button.dataset.variantId);
    const productId = Number(button.dataset.productId);
    const value = Number(button.dataset.price || 0) / 100;
    const gap = Number(root.dataset.gap || 0);
    if (!id || button.disabled || typeof window.__ottoCartWrite !== 'function') return;
    const body = new FormData();
    body.set('id', String(id));
    body.set('quantity', '1');
    const added = await window.__ottoCartWrite({ source: button, body,
      action: root.dataset.addUrl || `${routeRoot()}cart/add.js`,
      focusSelector: '[data-gap-add]:not(:disabled)' });
    if (added) {
      if (root.dataset.filter === 'mix') {
        publish('mix_discount_suggestion_add', {
          product_id: productId,
          value,
        });
      } else {
        publish('free_shipping_gap_closer_add', {
          gap,
          product_id: productId,
          value,
        });
      }
    }
  };

  const bindGapCloser = (root) => {
    if (!root || root.dataset.gapBound === '1') return;
    root.dataset.gapBound = '1';
    if (root.dataset.filter !== 'mix' && dismissed()) {
      root.hidden = true;
      return;
    }
    root.querySelector('[data-gap-dismiss]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (_) { /* Storage may be blocked. */ }
      root.hidden = true;
    });
    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-gap-add]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      addGapProduct(button, root);
    });
    loadGapCloser(root);
  };

  const updateProductProgress = (cart) => {
    document.querySelectorAll('[data-free-shipping-root][data-context="product"]').forEach((root) => {
      const threshold = Number(root.dataset.threshold || 0);
      const total = Number(cart.total_price || 0);
      const count = Number(cart.item_count || 0);
      if (!threshold) return;
      root.hidden = count < 1;
      let remaining = threshold - total;
      if (remaining < 0) remaining = 0;
      let progress = threshold > 0 ? Math.min(100, Math.round((total * 100) / threshold)) : 0;
      const fill = root.querySelector('[data-free-shipping-fill]');
      const text = root.querySelector('[data-free-shipping-text]');
      const track = root.querySelector('[role="progressbar"]');
      const dualTpl = root.dataset.msgDual || '';
      const remainTpl = root.dataset.msgRemaining || '';
      const achievedTpl = root.dataset.msgAchieved || '';
      const moneyFormat = root.dataset.moneyFormat;
      const from = Number(root.dataset.from || 0);
      const remainingRatio = threshold > 0 ? remaining / threshold : 1;
      const achieved = remaining === 0 && total > 0;
      const dual = total === 0 || remainingRatio > 0.6;
      root.classList.toggle('cart-free-shipping-progress--achieved', achieved);
      if (fill) {
        fill.style.width = `${progress}%`;
        fill.classList.toggle('cart-free-shipping-progress__fill--complete', achieved);
      }
      if (track) track.setAttribute('aria-valuenow', String(total));
      if (text) {
        if (achieved) {
          text.textContent = achievedTpl;
        } else if (dual) {
          text.textContent = dualTpl
            .split('__THRESHOLD__')
            .join(formatMoney(threshold, moneyFormat))
            .split('__FROM__')
            .join(formatMoney(from, moneyFormat));
        } else {
          text.textContent = remainTpl.split('__AMOUNT__').join(formatMoney(remaining, moneyFormat));
        }
      }
    });
  };

  const initFreeShipping = () => {
    document.querySelectorAll('[data-gap-closer]').forEach(bindGapCloser);
    document.querySelectorAll('[data-mix-progress]').forEach((root) => {
      if (root.dataset.viewed === '1') return;
      root.dataset.viewed = '1';
      publish('mix_discount_progress_view', {
        eligible_qty: Number(root.dataset.eligibleQty || 0),
        remaining: Number(root.dataset.remaining || 0),
      });
    });
    if (window.location.pathname.indexOf('/products/') !== -1) {
      fetch(`${routeRoot()}cart.js`)
        .then((response) => response.json())
        .then(updateProductProgress)
        .catch(() => {});
    }
  };

  window.__ottoInitFreeShipping = initFreeShipping;

  const wrapFetch = () => {
    const original = window.fetch;
    if (!original || original.__ottoFreeShipping) return;
    const wrapped = function wrappedFetch(input, init) {
      const url = typeof input === 'string' ? input : input?.url || input?.href || '';
      const method = (init?.method || input?.method || 'GET').toUpperCase();
      const isWrite = CART_WRITE.test(url) && method !== 'GET';
      return original.apply(this, arguments).then((response) => {
        if (isWrite) {
          window.setTimeout(initFreeShipping, 300);
        }
        return response;
      });
    };
    wrapped.__ottoFreeShipping = true;
    window.fetch = wrapped;
  };

  wrapFetch();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFreeShipping);
  } else {
    initFreeShipping();
  }
})();
